// Renderer de markdown liviano para las burbujas de chat (Director de Estudio
// + Asistente de marketing). Sin dependencias externas.
//
// Soporta: **bold**, *italic*, _italic_, `code`, [label](url), listas
// ordenadas / no ordenadas, headings (#..######), tablas GFM, y preserva los
// saltos de línea.
//
// Se comparte entre VideoEditor (Director) y ChatPanelV2 (Asistente) para que
// ambos rendericen EXACTAMENTE igual.

import React from 'react';

export const renderInlineMarkdown = (
  s: string,
): (string | React.ReactElement)[] => {
  const out: (string | React.ReactElement)[] = [];
  const re =
    /(\*\*[^*\n]+\*\*)|(`[^`\n]+`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*[^*\n]+\*)|(_[^_\n]+_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1]) out.push(<strong key={key++}>{m[1].slice(2, -2)}</strong>);
    else if (m[2])
      out.push(
        <code
          key={key++}
          className="px-1 py-0.5 rounded bg-black/10 text-[0.9em] font-mono"
        >
          {m[2].slice(1, -1)}
        </code>,
      );
    else if (m[3])
      out.push(
        <a
          key={key++}
          href={m[5]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80"
        >
          {m[4]}
        </a>,
      );
    else if (m[6]) out.push(<em key={key++}>{m[6].slice(1, -1)}</em>);
    else if (m[7]) out.push(<em key={key++}>{m[7].slice(1, -1)}</em>);
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
};

/** Dominio (sin www) de una URL. Devuelve la URL cruda si no parsea. */
const hostnameOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/** Card de preview para un link que va solo en su línea: favicon + título +
 *  dominio, en un contenedor (en vez del texto azul subrayado). El favicon
 *  sale del servicio de Google, sin fetchear la página. */
const renderLinkCard = (
  url: string,
  label: string,
  key: number,
): React.ReactElement => {
  const host = hostnameOf(url);
  const favicon = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(
    host,
  )}&sz=64`;
  const title = label || host;
  return (
    <a
      key={key}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block my-2 no-underline rounded-xl border border-black/[0.08] bg-black/[0.02] hover:bg-black/[0.05] hover:border-black/[0.14] transition-colors px-3 py-2.5"
    >
      <span className="flex items-center gap-2.5">
        <img
          src={favicon}
          alt=""
          loading="lazy"
          className="w-5 h-5 rounded shrink-0"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
        <span className="min-w-0 flex flex-col leading-tight">
          <span className="text-[14px] font-medium text-brand-black/90 truncate">
            {title}
          </span>
          <span className="text-[12px] text-[#3C3C43]/55 truncate">{host}</span>
        </span>
        <span className="ml-auto text-[#3C3C43]/40 text-[13px] shrink-0">↗</span>
      </span>
    </a>
  );
};

export const renderMarkdown = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  const blocks: React.ReactElement[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trimStart();
    // Unordered list
    if (/^(-|\*)\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^(-|\*)\s+/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^(-|\*)\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={k++} className="list-disc pl-5 space-y-0.5 my-1">
          {items.map((it, idx) => (
            <li key={idx}>{renderInlineMarkdown(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    // Ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={k++} className="list-decimal pl-5 space-y-0.5 my-1">
          {items.map((it, idx) => (
            <li key={idx}>{renderInlineMarkdown(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }
    // GFM table: a header row of pipes followed by a separator row like
    // | --- | :---: | ---: |. Body rows continue until a non-pipe line.
    const isPipeRow = (s: string) => /^\s*\|.*\|\s*$/.test(s);
    const isSeparator = (s: string) =>
      /^\s*\|?\s*:?-{3,}:?(\s*\|\s*:?-{3,}:?)+\s*\|?\s*$/.test(s);
    if (isPipeRow(line) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      // Un `\|` es un pipe DENTRO de la celda (GFM), no un separador: los nombres de
      // campañas de Meta vienen así ("PURCHASE | Adv+ | Sep26") y partían la fila.
      const splitRow = (s: string) => {
        const body = s.trim().replace(/^\|/, '').replace(/(^|[^\\])\|$/, '$1');
        const cells: string[] = [];
        let cur = '';
        for (let j = 0; j < body.length; j++) {
          if (body[j] === '\\' && body[j + 1] === '|') {
            cur += '|';
            j++;
          } else if (body[j] === '|') {
            cells.push(cur.trim());
            cur = '';
          } else {
            cur += body[j];
          }
        }
        cells.push(cur.trim());
        return cells;
      };
      const headers = splitRow(line);
      const aligns = splitRow(lines[i + 1]).map((spec) => {
        const l = spec.startsWith(':');
        const r = spec.endsWith(':');
        if (l && r) return 'center';
        if (r) return 'right';
        return 'left';
      });
      i += 2;
      const rows: string[][] = [];
      while (
        i < lines.length &&
        isPipeRow(lines[i]) &&
        !isSeparator(lines[i])
      ) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push(
        <div
          key={k++}
          className="my-2 overflow-x-auto rounded-md border border-black/[0.08]"
        >
          <table className="w-full text-[13px] border-collapse">
            <thead className="bg-black/[0.03]">
              <tr>
                {headers.map((h, hi) => (
                  <th
                    key={hi}
                    className="px-3 py-2 font-semibold text-brand-black/80 border-b border-black/[0.08]"
                    style={{
                      textAlign: aligns[hi] as 'left' | 'center' | 'right',
                    }}
                  >
                    {renderInlineMarkdown(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr
                  key={ri}
                  className={
                    ri < rows.length - 1 ? 'border-b border-black/[0.06]' : ''
                  }
                >
                  {r.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-2 align-top"
                      style={{
                        textAlign: aligns[ci] as 'left' | 'center' | 'right',
                      }}
                    >
                      {renderInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    // Heading
    const hMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (hMatch) {
      const level = Math.min(hMatch[1].length + 2, 6);
      const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;
      blocks.push(
        React.createElement(
          Tag,
          { key: k++, className: 'font-semibold my-1' },
          renderInlineMarkdown(hMatch[2]),
        ),
      );
      i++;
      continue;
    }
    // Blank line → small spacer (acts as paragraph break)
    if (line.trim() === '') {
      blocks.push(<div key={k++} className="h-2" />);
      i++;
      continue;
    }
    // Link solo en su línea → card de preview (favicon + dominio). Los links
    // inline dentro de una oración se dejan como texto (renderInlineMarkdown).
    const lt = line.trim();
    const mdLink = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(lt);
    const bareLink = /^(https?:\/\/\S+)$/.exec(lt);
    if (mdLink || bareLink) {
      const url = mdLink ? mdLink[2] : bareLink![1];
      const label = mdLink ? mdLink[1] : '';
      blocks.push(renderLinkCard(url, label, k++));
      i++;
      continue;
    }
    // Paragraph
    blocks.push(
      <p key={k++} className="my-0 whitespace-pre-wrap break-words">
        {renderInlineMarkdown(line)}
      </p>,
    );
    i++;
  }
  return <>{blocks}</>;
};
